// Fleet Management — the designer's agate presentation (clean header, KpiCard stat
// row, per-host cards + sandbox tables) wired to OUR live /api/fleet data: real
// `enforcing` never hardcoded, 401 → sign-in, a hard error → an honest "can't
// reach the backend" state (never fabricated hosts/sandboxes — this is a real
// customer's own infrastructure, not a demo). The designer's page was 100%
// mock-state; this replaces that with the adapter below.
import { useCallback, useEffect, useState } from "react";
import { Flex, Button, Spin, message } from "antd";
import { Plus } from "lucide-react";
import { KpiCard } from "@bluerock/corenet";
import { HostCard } from "./HostCard";
import { ManagementHostCard, type ManagementHostCa, type ManagementHostSso } from "./ManagementHostCard";
import type { Host, Sandbox } from "../model/fleetTypes";
import { displayFullName, displayEmail } from "@/lib/displayName";
import { useFleetOps } from "@/components/fleet/useFleetOps";
import ConnectModal, { type ConnectHost } from "@/components/fleet/ConnectModal";
import ProvisionSshSandboxModal from "@/components/fleet/ProvisionSshSandboxModal";
import ManageSshAccessModal from "@/components/fleet/ManageSshAccessModal";
import PageHeader from "@/components/PageHeader";
import { fleetApi, type Size, SIZE_LABEL } from "@/lib/api/fleet";


// ── raw /api/fleet shapes (loosely typed; fields absent on older fleetd) ──
type RawHost = {
  name: string; region?: string; size?: string; vcpu?: number; memGiB?: number;
  cpuPct?: number; memPct?: number; uptime?: string; enforcing?: boolean; kernel?: string;
  role?: string; schedulable?: boolean; ip?: string; publicIp?: string;
  policy?: { tiers?: string[] };
};
type RawSandbox = {
  id: string; name?: string; workspaceId?: string; image: string; host: string;
  users?: string[]; uptime?: string; created?: string; tier?: string; state?: string;
  egress?: string; sessions?: number; clients?: string[];
};
type RawFleet = {
  sso?: ManagementHostSso; ca?: ManagementHostCa; connect?: unknown; maxPerHost?: number;
  hosts: RawHost[]; sandboxes: RawSandbox[];
};

function cloudOf(region = ""): string {
  if (/^(us|eu|ap|sa|ca|af|me)-\w+-\d/.test(region)) return "AWS";
  if (/^(east|west|north|south|central)/i.test(region) || /europe|asia/i.test(region)) return "Azure";
  return "Cloud";
}

function adaptHost(h: RawHost, maxPerHost: number): Host {
  const control = h.role === "control-plane" || h.schedulable === false;
  return {
    id: h.name, name: h.name, cloud: cloudOf(h.region), region: h.region || "—",
    instanceSize: h.size || "—", sandboxCapacity: maxPerHost, kernel: h.kernel || "—",
    enforcing: !!h.enforcing, role: control ? "control-plane" : "worker", schedulable: h.schedulable !== false,
    uptime: h.uptime || "—", vcpu: h.vcpu ?? 0, memGiB: h.memGiB ?? 0,
    cpuPercent: h.cpuPct ?? 0, memPercent: h.memPct ?? 0, policyTier: h.policy?.tiers?.[0], ip: h.ip, publicIp: h.publicIp,
  };
}
function adaptSandbox(s: RawSandbox): Sandbox {
  const principal = s.users?.[0] ?? "";
  return {
    id: s.id, hostId: s.host, ownerName: displayFullName(principal), ownerEmail: displayEmail(principal),
    principal, tier: s.tier || "default", image: s.image, workspaceId: s.workspaceId ?? null,
    status: (s.state === "running" || (s.sessions ?? 0) > 0) ? "running" : "idle",
    ageLabel: s.uptime || s.created || "—", sessions: s.sessions ?? 0, clients: s.clients ?? [],
    egress: s.tier === "citizen-dev" ? s.egress : undefined,
  };
}

export default function FleetPage() {
  const [live, setLive] = useState<RawFleet | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "error" | "auth">("loading");

  const load = useCallback(() => {
    const tok = localStorage.getItem("bluerock.token");
    return fetch("/api/fleet", { cache: "no-store", headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { setLive(d); setStatus("ok"); })
      .catch((e) => setStatus((s) => (s === "ok" ? "ok" : String((e as Error)?.message) === "401" ? "auth" : "error")));
  }, []);
  const ops = useFleetOps(load);
  const [provisionOpen, setProvisionOpen] = useState(false);
  // Always set before provisionOpen flips true — the only trigger left is a
  // specific host's own "Create SSH Sandbox" button (the page-level CTA that
  // used to leave this undefined was removed 2026-09-09).
  const [provisionHostId, setProvisionHostId] = useState<string | undefined>();
  const [manageAccessOpen, setManageAccessOpen] = useState(false);
  const [manageAccessHostId, setManageAccessHostId] = useState<string | undefined>();
  // Per-host, not page-level — Dev host is a REQUIRED, specific address
  // (self-hosted, never auto-discovered), and since a host enforces exactly
  // one policy, WHICH host the admin hands out as Dev host is what actually
  // gets a user onto the right policy. A page-level/host-agnostic version
  // can't answer "which host" at all, so it doesn't serve the real need.
  // (Reverses the 2026-09-09 page-level move once this became clear;
  // ConnectModal's own focusHost-based per-host address resolution was
  // never removed, just its only caller stopped passing a host.)
  const [connectHost, setConnectHost] = useState<ConnectHost | null>(null);
  useEffect(() => { load(); const t = window.setInterval(load, 20000); return () => window.clearInterval(t); }, [load]);

  // The fleet's size catalog (Small/Medium/Large — the same vocabulary the
  // Add-host/Resize picker uses), fetched once so each host's sandbox capacity
  // can be traced back to its actual size instead of a bare cloud SKU string.
  const [sizes, setSizes] = useState<Size[]>([]);
  useEffect(() => { fleetApi.sizes().then(setSizes).catch(() => {}); }, []);
  // memGiB tolerance: cloud providers commonly report slightly less than a
  // VM's nominal spec (hypervisor/OS reservation) — e.g. Azure's real
  // Standard_D8as_v7 (nominal 32 GiB) reports 31. vCPU stays an exact match
  // (that's always a clean integer); the curated tiers are spaced 8+ GiB
  // apart, so a ±2 GiB tolerance can't cross into the wrong tier.
  const sizeFor = useCallback(
    (h: { vcpu: number; memGiB: number }) =>
      sizes.find((s) => s.vcpu === h.vcpu && Math.abs(s.memGiB - h.memGiB) <= 2),
    [sizes],
  );

  const act = async (principal: string, verb: "stop" | "destroy") => {
    try {
      const tok = localStorage.getItem("bluerock.token");
      const r = await fetch(`/api/sandbox/${verb}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({ principal }),
      });
      const j = await r.json();
      if (j.ok) { message.success(verb === "stop" ? "Sandbox stopped" : "Sandbox destroyed"); setTimeout(load, 900); }
      else message.error(j.message || `Couldn't ${verb} the sandbox`);
    } catch { message.error("Request failed — the fleet backend didn't respond. Try again."); }
  };

  if (live === null && status === "auth") {
    return (
      <>
        <PageHeader title="Host Fleet" />
        <Flex vertical align="center" gap="middle" style={{ padding: "48px 0", textAlign: "center" }}>
          <p style={{ margin: 0 }}>Your session expired. Sign in again to view the live fleet.</p>
          <Button type="primary" onClick={() => { window.location.href = "/login"; }}>Sign in</Button>
        </Flex>
      </>
    );
  }
  if (live === null && status === "error") {
    return (
      <>
        <PageHeader title="Host Fleet" />
        <Flex vertical align="center" gap="middle" style={{ padding: "48px 0", textAlign: "center" }}>
          <p style={{ margin: 0 }}>Can't reach the fleet backend. Hosts and sandboxes will show once the connection is restored.</p>
          <Button type="primary" onClick={() => load()}>Retry</Button>
        </Flex>
      </>
    );
  }
  if (!live) {
    return <div style={{ padding: "48px 0", textAlign: "center" }}><Spin /> <span style={{ marginLeft: 8 }}>Loading fleet…</span></div>;
  }

  // maxPerHost is a flat fleet-wide config value (max_sandboxes_per_node) —
  // used only as a fallback for a host whose vcpu/memGiB doesn't match any
  // entry in the size catalog (catalog empty/not yet loaded, or a size that's
  // since been retired). Where a match exists, that size's own recommended
  // capacity (Size.sandboxes) is the real number — a host's cap should trace
  // back to what it's actually sized to run, not one number applied fleet-wide.
  const maxPerHost = live.maxPerHost ?? 2;
  const hosts = live.hosts.map((h) => {
    const host = adaptHost(h, maxPerHost);
    const size = sizeFor(host);
    return size?.sandboxes ? { ...host, sandboxCapacity: size.sandboxes } : host;
  });
  const sandboxes = live.sandboxes.map(adaptSandbox);
  const byHost = (id: string) => sandboxes.filter((s) => s.hostId === id);
  const workers = hosts.filter((h) => h.role !== "control-plane");
  const managementHost = hosts.find((h) => h.role === "control-plane");
  const totalCap = workers.reduce((sum, h) => sum + h.sandboxCapacity, 0);
  const sandboxCount = sandboxes.length;
  const runningCount = sandboxes.filter((s) => s.status === "running").length;
  const developers = new Set(sandboxes.map((s) => s.principal)).size;
  const slotsFree = totalCap - sandboxCount;
  const hostsAtCap = workers.filter((h) => byHost(h.id).length >= h.sandboxCapacity).length;

  // The control-plane box always renders via ManagementHostCard, driven by
  // the fleet-wide ca/sso blocks /api/fleet returns — those describe this
  // host regardless of whether fleetd also lists it as a Host entry. This
  // used to synthesize a fallback entry only "while no REAL host carries
  // that role"; now that fleetd reports control-plane for real (2026-09-10)
  // and is expected to keep doing so, that fallback framing no longer
  // applies — ManagementHostCard is simply the right shape for this host,
  // full stop. `workers` (control-plane excluded) is what the generic
  // HostCard loop below renders, so the control-plane host never also
  // shows up there with the wrong fields (Public IP/Cloud/Region/etc).
  const showManagementHostCard = !!live.ca?.host;

  // Deploy-SSH-sandbox modal's host picker — workers only (sandboxes never
  // schedule on the control-plane host), with the fields it needs to show
  // free capacity and each host's one policy tier without a second fetch.
  const provisionHostOptions = workers.map((h) => ({
    id: h.id, name: h.name, ip: h.ip, policyTier: h.policyTier,
    capacity: h.sandboxCapacity, used: byHost(h.id).length,
  }));

  return (
    <>
      <Flex vertical gap="middle" style={{ paddingBottom: 80 }}>
        <PageHeader
          title="Host Fleet"
          actions={
            <Flex gap={8}>
              <Button icon={<Plus size={14} />} onClick={ops.openAddHost}>Add Host</Button>
            </Flex>
          }
        />

        <Flex gap={16} wrap="wrap" align="stretch">
          <KpiCard title="Hosts" value={hosts.length} suffix="running" />
          <KpiCard title="Sandboxes" value={sandboxCount} suffix="provisioned" />
          <KpiCard title="Active" value={`${runningCount}/${sandboxCount}`} suffix="sandboxes active" />
          <KpiCard title="Users" value={developers} suffix="with a sandbox" />
          <KpiCard title="Fleet capacity" value={`${slotsFree}/${totalCap}`} suffix="slots available"
            footnote={hostsAtCap ? `${hostsAtCap} host${hostsAtCap === 1 ? "" : "s"} at cap` : undefined} />
        </Flex>

        {showManagementHostCard && live.ca && (
          <ManagementHostCard name={managementHost?.name} ca={live.ca} sso={live.sso} bootstrapUrl={(live.connect as ConnectModalConnect)?.bootstrap_url} />
        )}

        {workers.map((h) => {
          const raw = live.hosts.find((rh) => rh.name === h.id);
          const size = sizeFor(h);
          return (
            <HostCard
              key={h.id}
              host={h}
              sandboxes={byHost(h.id)}
              onAction={act}
              onResize={ops.resizeHost}
              onRemove={ops.removeHost}
              onConnect={() => setConnectHost({ name: h.id, publicIp: raw?.publicIp, role: raw?.role, policyTier: h.policyTier, sandboxCapacity: h.sandboxCapacity, sandboxesUsed: byHost(h.id).length })}
              onDeploySshSandbox={() => { setProvisionHostId(h.id); setProvisionOpen(true); }}
              onManageSshAccess={() => { setManageAccessHostId(h.id); setManageAccessOpen(true); }}
              sizeTier={size ? (SIZE_LABEL[size.size] ?? size.size) : undefined}
            />
          );
        })}
      </Flex>

      {ops.portals}
      <ConnectModal
        open={!!connectHost}
        onClose={() => setConnectHost(null)}
        connect={live.connect as ConnectModalConnect}
        hosts={live.hosts as ConnectHost[]}
        focusHost={connectHost}
      />
      <ProvisionSshSandboxModal open={provisionOpen} onClose={() => setProvisionOpen(false)} onChanged={load}
        hosts={provisionHostOptions} lockedHostId={provisionHostId} />
      <ManageSshAccessModal open={manageAccessOpen} onClose={() => setManageAccessOpen(false)} onChanged={load}
        hosts={provisionHostOptions} hostId={manageAccessHostId ?? ""} />
    </>
  );
}

// ConnectModal's `connect` prop type (imported shape) — kept loose here.
type ConnectModalConnect = React.ComponentProps<typeof ConnectModal>["connect"];
